import { Suspense, lazy, useEffect, useState, useCallback } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { setEnvBarUserInfo } from '@elli/ui';
import { TenantProvider, useTenant } from './contexts/TenantContext';
import { ProfileProvider, useProfile } from './contexts/ProfileContext';
import { FeaturesProvider } from './contexts/FeaturesContext';
import { OnboardingProvider } from './contexts/OnboardingContext';
import { ImpersonationProvider } from './contexts/ImpersonationContext';
import { ImpersonationBanner } from './components/ImpersonationBanner';
import { DashboardLayout } from './components/layout/DashboardLayout';
import { ProtectedRoute } from './components/ProtectedRoute';
import { ForbiddenPage } from './components/ForbiddenPage';
import { InternalOnlyForbiddenPage } from './components/InternalOnlyForbiddenPage';
import { SessionErrorBoundary } from './components/SessionErrorBoundary';
import routes from './routes';
import type { StoredSession } from './types';
import { MembershipConsent } from './routes/pages/MembershipConsent';
import { canAccessAppFrontend, isInternalOnlyTenant } from './utils/accessControl';
import { OnboardingWizardRedirect } from './components/onboarding-flow/wizard/OnboardingWizardRedirect';
import { fetchCurrentSession } from './utils/sessionFetch';
import i18n from './i18n/config';
import { isLanguage } from './i18n/types';
import { identifyUser, trackEvent, resetMixpanel, setPersona, stopSessionRecording, startSessionRecording } from './mixpanel';
import { classifyPersona } from './personaClassifier';

const TenantSelection = lazy(() => import('./routes/pages/TenantSelection'));
const Login = lazy(() => import('./routes/pages/Login'));
const AcceptInvite = lazy(() => import('./routes/pages/AcceptInvite'));
const Onboarding = lazy(() => import('./routes/pages/Onboarding'));
const MyProfile = lazy(() => import('./routes/pages/MyProfile'));
const PhoneScan = lazy(() => import('./routes/pages/PhoneScan'));
const LegalDocument = lazy(() => import('./routes/pages/LegalDocument'));
const SetupWizard = lazy(() => import('./routes/pages/SetupWizard'));
const MagicLinkSurvey = lazy(() => import('./routes/pages/MagicLinkSurvey'));
const GlossaryPage = lazy(() => import('./routes/pages/GlossaryPage'));
const StartOnboarding = lazy(() => import('./routes/pages/StartOnboarding'));
const OnboardingImport = lazy(() => import('./routes/pages/OnboardingImport'));
const OnboardingWizard = lazy(() => import('./routes/pages/OnboardingWizard'));
const OnboardingLinkStatus = lazy(() => import('./routes/OnboardingLinkStatus'));
// In-app survey wizard (`/insights/surveys/:surveyId/edit`). Mounted HERE,
// not under `InsightsDashboard`, because it renders its own standalone
// full-page shell — see the route below (EL-1174).
const SurveyEditWizardPage = lazy(() => import('./routes/pages/SurveyEditWizardPage'));

/**
 * Redirects legacy paths to new paths, preserving the sub-path.
 * e.g., /assist/surveys -> /insights/surveys
 */
const LegacyRedirect = ({ to }: { to: string }) => {
  const params = useParams();
  const subPath = params['*'] || '';
  const newPath = subPath ? `/${to}/${subPath}` : `/${to}`;
  return <Navigate to={newPath} replace />;
};

// Bridges in-provider tenant/profile state into the EnvIndicatorBar
// store (the bar itself is mounted higher up in main.tsx).
const EnvBarUserBridge = () => {
  const { currentTenant } = useTenant();
  const { profile } = useProfile();
  const tenantName = currentTenant?.tenantName;
  const userEmail = profile?.primary_email;
  const roles = currentTenant?.roles;
  useEffect(() => {
    setEnvBarUserInfo({ tenantName, userEmail, roles });
  }, [tenantName, userEmail, roles]);
  return null;
};

const App = () => {
  const location = useLocation();
  const [session, setSession] = useState<StoredSession | null | undefined>(undefined);

  // Public routes that don't require authentication
  const isPublicRoute =
    location.pathname === '/accept-invite' ||
    location.pathname === '/onboarding' ||
    location.pathname === '/onboarding/cancel' ||
    location.pathname === '/onboarding/expired' ||
    location.pathname.startsWith('/app/scan/') ||
    location.pathname.startsWith('/legal/') ||
    location.pathname === '/setup' ||
    location.pathname === '/start' ||
    location.pathname.startsWith('/survey/');

  // Refresh session callback (used after consent completion)
  const refreshSession = useCallback(async () => {
    try {
      const response = await fetchCurrentSession();
      if (response.ok) {
        const sessionData = await response.json();
        setSession(sessionData as StoredSession);
        // Store only the fields needed for pre-React client-side operations
        // (language detection + persona switcher). Full session is in React
        // state; we must not cache PII / employment data in localStorage.
        // GDPR Art. 25 data-minimisation — see issue #1642.
        localStorage.setItem('session', JSON.stringify({
          preferredLanguage: sessionData.preferredLanguage,
          accessibleLocations: sessionData.accessibleLocations,
        }));
        // Sync i18n language if preferredLanguage is set
        if (sessionData.preferredLanguage && isLanguage(sessionData.preferredLanguage)) {
          if (i18n.language !== sessionData.preferredLanguage) {
            i18n.changeLanguage(sessionData.preferredLanguage);
          }
        }
      }
    } catch (err) {
      console.error('Failed to refresh session:', err);
    }
  }, []);

  useEffect(() => {
    const checkSession = async () => {
      try {
        const response = await fetchCurrentSession();
        if (response.ok) {
          const sessionData = await response.json();
          setSession(sessionData as StoredSession);
          // Store only the fields needed for pre-React client-side operations
          // (language detection + persona switcher). Full session is in React
          // state; we must not cache PII / employment data in localStorage.
          // GDPR Art. 25 data-minimisation — see issue #1642.
          localStorage.setItem('session', JSON.stringify({
            preferredLanguage: sessionData.preferredLanguage,
            accessibleLocations: sessionData.accessibleLocations,
          }));
          // Sync i18n language if preferredLanguage is set
          if (sessionData.preferredLanguage && isLanguage(sessionData.preferredLanguage)) {
            if (i18n.language !== sessionData.preferredLanguage) {
              i18n.changeLanguage(sessionData.preferredLanguage);
            }
          }
          // Identify user in Mixpanel
          const storedSession = sessionData as StoredSession;
          const membership = storedSession.memberships.find(
            (m) => m.tenantId === storedSession.primaryTenantId
          );
          identifyUser(storedSession.userId, {
            email: storedSession.email ?? undefined,
            name: storedSession.displayName ?? undefined,
            tenantId: storedSession.primaryTenantId,
            tenantName: membership?.tenantName,
            roles: membership?.roles,
            preferredLanguage: storedSession.preferredLanguage,
          });
          // Only track Sign In on actual login, not on page refresh/tenant switch
          if (sessionStorage.getItem('mixpanel_sign_in')) {
            sessionStorage.removeItem('mixpanel_sign_in');
            trackEvent('Sign In', {
              user_id: storedSession.userId,
              login_method: 'otp',
              success: true,
            });
          }
        } else {
          setSession(null);
          // Clear session from localStorage on logout/invalid session
          localStorage.removeItem('session');
          resetMixpanel();
        }
      } catch {
        setSession(null);
        localStorage.removeItem('session');
        resetMixpanel();
      }
    };

    checkSession();
  }, []);

  // Control session recording based on authentication state.
  // Auth pages (email entry, OTP, passkey) must not be recorded — session
  // recorders can capture keystrokes on form fields. GDPR Art. 25: collect
  // only what is necessary. Recording resumes once the user is authenticated.
  useEffect(() => {
    if (session === null) {
      stopSessionRecording();
    } else if (session) {
      startSessionRecording();
    }
    // session === undefined means still loading; leave recording state unchanged.
  }, [session]);

  useEffect(() => {
    const persona = classifyPersona(location.pathname);
    if (persona) setPersona(persona);
  }, [location.pathname]);

  // Show loading while checking session (except for public routes)
  if (session === undefined && !isPublicRoute) {
    return <div>Loading...</div>;
  }

  // Always show public routes regardless of auth status
  if (isPublicRoute) {
    return (
      <Suspense fallback={<div>Loading...</div>}>
        <Routes>
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/onboarding/cancel" element={<OnboardingLinkStatus />} />
          <Route path="/onboarding/expired" element={<OnboardingLinkStatus expired />} />
          <Route path="/app/scan/:token" element={<PhoneScan />} />
          <Route path="/legal/:type" element={<LegalDocument />} />
          <Route path="/setup" element={<SetupWizard />} />
          <Route path="/start" element={<StartOnboarding />} />
          <Route path="/survey/:token" element={<MagicLinkSurvey />} />
        </Routes>
      </Suspense>
    );
  }

  // Show login if no session
  if (!session) {
    return (
      <Suspense fallback={<div>Loading...</div>}>
        <Routes>
          <Route path="*" element={<Login />} />
        </Routes>
      </Suspense>
    );
  }

  // Check if user needs to accept/re-accept consent (EL-784).
  // The needsReconsent flag is computed server-side based on consent version
  // comparison. Today the only platform consent is the optional, declinable
  // "Connect your wellness tools with Elli" — it's only relevant to the
  // self-care surface, so the gate is scoped to /self-care instead of every
  // authenticated route. Gating everything interrupted the PLG signup funnel
  // (every fresh activation hit this interstitial before /onboarding/import);
  // deferring it to first self-care use asks for it in context instead.
  const isSelfCareRoute =
    location.pathname === '/self-care' || location.pathname.startsWith('/self-care/');
  if (session.needsReconsent && isSelfCareRoute) {
    return (
      <MembershipConsent
        activeConsentVersion={session.activeConsentVersion}
        preferredLanguage={session.preferredLanguage}
        onComplete={refreshSession}
      />
    );
  }

  // Check if user can access the app frontend at all (based on roles in primary tenant)
  const currentMembership = session.memberships.find(
    (m) => m.tenantId === session.primaryTenantId
  );
  const currentRoles = currentMembership?.roles || [];

  if (!canAccessAppFrontend(currentRoles)) {
    return (
      <SessionErrorBoundary>
        <TenantProvider>
          <ForbiddenPage
            message="Your account doesn't have access to this application. Please contact your administrator."
            showTenantSwitcher={session.memberships.length > 1}
          />
        </TenantProvider>
      </SessionErrorBoundary>
    );
  }

  // Check if current tenant is internal-only (e.g., tenant-internal)
  // Internal-only tenants cannot access /app/* routes
  if (isInternalOnlyTenant(currentMembership ?? null)) {
    return (
      <SessionErrorBoundary>
        <TenantProvider>
          <InternalOnlyForbiddenPage
            memberships={session.memberships}
          />
        </TenantProvider>
      </SessionErrorBoundary>
    );
  }

  // Onboarding funnel pages (EL-718): a freshly activated self-serve tenant
  // lands here from the activation email. Full-bleed, no dashboard shell —
  // the tenant has no survey and no people yet, so the normal app would be
  // an empty dashboard. Requires a session (unauthenticated visits fell into
  // the Login catch-all above).
  if (
    location.pathname === '/onboarding/import' ||
    location.pathname === '/onboarding/review' ||
    location.pathname === '/onboarding/survey' ||
    location.pathname === '/onboarding/wizard'
  ) {
    return (
      <SessionErrorBoundary>
        <Suspense fallback={<div>Loading...</div>}>
          <Routes>
            <Route
              path="/onboarding/import"
              element={
                <OnboardingImport
                  tenantId={session.primaryTenantId}
                  tenantName={currentMembership?.tenantName ?? ''}
                />
              }
            />
            <Route path="/onboarding/review" element={<OnboardingWizardRedirect step="review" />} />
            <Route path="/onboarding/survey" element={<OnboardingWizardRedirect step="survey" />} />
            <Route
              path="/onboarding/wizard"
              element={<OnboardingWizard tenantName={currentMembership?.tenantName ?? ''} />}
            />
          </Routes>
        </Suspense>
      </SessionErrorBoundary>
    );
  }

  // Show app routes if logged in and all consents given
  return (
    <SessionErrorBoundary>
      <TenantProvider>
        <ProfileProvider>
          <EnvBarUserBridge />
          <FeaturesProvider>
            <OnboardingProvider>
              <ImpersonationProvider>
                <Routes>
                  {/* Full-page survey wizard (EL-1174).
                      `SurveyEditWizardPage` renders `SurveyEditShell`, a
                      STANDALONE page shell — own full-bleed background, own
                      top bar with an exit control and a language switcher,
                      `min-h-screen`. It used to be registered inside
                      `InsightsDashboard`, i.e. nested in `DashboardLayout`,
                      which supplies its own sidebar, header (with its own
                      language switcher) and padded `<main>`. Two full-page
                      chromes nested gave a duplicated language bar, a cream
                      slab inset inside the padded content area, and ~120px of
                      permanent phantom scroll. Mounting it here — a sibling of
                      the dashboard, still inside every provider — restores the
                      same layout the onboarding route gets. Ranks above the
                      `*` route below: React Router scores static/dynamic
                      segments over a splat regardless of order. */}
                  <Route
                    path="/insights/surveys/:surveyId/edit"
                    element={
                      <ProtectedRoute>
                        {/* Kept even though the dashboard chrome is gone: this
                            page can launch a survey to the whole company, so a
                            staff member acting as someone else must still see
                            it. Renders null when not impersonating. */}
                        <ImpersonationBanner />
                        <Suspense fallback={<div>Loading...</div>}>
                          <SurveyEditWizardPage />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  {/* Everything else keeps the dashboard chrome. */}
                  <Route
                    path="*"
                    element={
                      <DashboardLayout>
                        <ImpersonationBanner />
                        <Suspense fallback={<div>Loading...</div>}>
                          <Routes>
                            <Route index element={<TenantSelection />} />
                            <Route path="/insights/profile" element={<ProtectedRoute><MyProfile /></ProtectedRoute>} />
                            <Route path="/self-care/profile" element={<ProtectedRoute><MyProfile /></ProtectedRoute>} />
                            {/* Global glossary (EL-569): a top-level route reachable
                                from anywhere in the app via the header button. */}
                            <Route path="/glossary" element={<ProtectedRoute><GlossaryPage /></ProtectedRoute>} />
                            {/* The /admin section is unmaintained and hidden (EL-502).
                                Redirect every /admin/* path (including the old
                                /admin/profile) to /insights so stale links/bookmarks
                                land somewhere valid. */}
                            <Route path="/admin/*" element={<Navigate to="/insights" replace />} />
                            {routes.map((route) => (
                              <Route
                                key={route.path}
                                path={`${route.path}/*`}
                                element={
                                  <ProtectedRoute>
                                    <route.component />
                                  </ProtectedRoute>
                                }
                              />
                            ))}
                            {/* Legacy path redirects (e.g., /assist/* -> /insights/*) */}
                            {routes
                              .filter((route) => route.legacyPath)
                              .map((route) => (
                                <Route
                                  key={`legacy-${route.legacyPath}`}
                                  path={`${route.legacyPath}/*`}
                                  element={<LegacyRedirect to={route.path} />}
                                />
                              ))}
                            <Route path="*" element={<Navigate to="/" replace />} />
                          </Routes>
                        </Suspense>
                      </DashboardLayout>
                    }
                  />
                </Routes>
              </ImpersonationProvider>
            </OnboardingProvider>
          </FeaturesProvider>
        </ProfileProvider>
      </TenantProvider>
    </SessionErrorBoundary>
  );
};

export default App;
